import { Suspense, useState } from "react";
import { MessageArea } from "@/components/layout/MessageArea";
import { MULTIPLEX_CAP } from "./use-multiplex-mounted-ids";

interface MultiplexLayerProps {
  selectedChannel: string | null;
  selectedWorkspace: string | null;
  isMultiplexActive: boolean;
  mountedIds: ReadonlyArray<string>;
  channelWorkspaceMap: ReadonlyMap<string, string | null>;
  targetMessageId: string | null;
  targetThreadId: string | null;
  handleSelectChannelForMessageArea: (id: string) => void;
  handleToggleTerminal: () => void | Promise<void>;
  handleTargetMessageHandled: () => void;
  /** Mobile only — forwarded to MessageAreaHeader's hamburger button. */
  onOpenMobileSidebar?: () => void;
}

/**
 * The persistent multiplex MessageArea layer.
 *
 * Toggled visible/hidden via className so kept-alive channels survive
 * top-level view switches (Tables, Team, a List, Inbox, full-focus
 * terminal). When `!isMultiplexActive`, every MessageArea inside
 * receives `hidden={true}`, which the existing per-surface hidden
 * guards use to gate side-effects (keydown listeners, intervals, warm
 * preload, etc.).
 *
 * `MULTIPLEX_CAP === 1` collapses to a single-mount path where one
 * persistent MessageArea has its `channelId` prop swapped on each
 * switch — same React fiber survives across switches, so in-component
 * refs (scrollAnchorByChannel, composer drafts, etc.) persist
 * naturally. Bumping `MULTIPLEX_CAP` re-enables the keyed-list branch.
 */
export function MultiplexLayer({
  selectedChannel,
  selectedWorkspace,
  isMultiplexActive,
  mountedIds,
  channelWorkspaceMap,
  targetMessageId,
  targetThreadId,
  handleSelectChannelForMessageArea,
  handleToggleTerminal,
  handleTargetMessageHandled,
  onOpenMobileSidebar,
}: MultiplexLayerProps) {
  const isKnownDmChannel = (id: string | null) =>
    Boolean(
      id && channelWorkspaceMap.has(id) && channelWorkspaceMap.get(id) === null,
    );

  // Sticky channel id — keep the LAST non-null `selectedChannel` so
  // that navigating to a top-level view that clears the channel cursor
  // (Inbox / Lists / Coworkers / Automations / etc., all of which call
  // `setSelectedChannel(null)`) does NOT unmount MessageArea. The
  // wrapper's `hidden` class still hides the message area visually
  // (and the `hidden={!isMultiplexActive}` prop gates side effects),
  // but the React fiber survives — so `scrollAnchorByChannel`, composer
  // drafts, and every other in-component ref persist across the
  // round-trip. Without this, the CAP=1 mount path would tear down
  // and remount every time the user dipped into Inbox.
  //
  // The state update is adjusted DURING render (React's
  // "Adjusting state during render" pattern) rather than via
  // useEffect — eliminates a wasted render cycle and avoids the
  // react-hooks/set-state-in-effect rule.
  const [stickyChannelId, setStickyChannelId] = useState<string | null>(
    selectedChannel,
  );
  if (selectedChannel !== null && selectedChannel !== stickyChannelId) {
    setStickyChannelId(selectedChannel);
  }

  return (
    <div className={isMultiplexActive ? "contents" : "hidden"}>
      {MULTIPLEX_CAP === 1
        ? stickyChannelId && (
            <Suspense fallback={null}>
              <MessageArea
                channelId={stickyChannelId}
                workspaceId={selectedWorkspace}
                onSelectChannel={handleSelectChannelForMessageArea}
                onToggleTerminal={handleToggleTerminal}
                onOpenMobileSidebar={onOpenMobileSidebar}
                targetMessageId={targetMessageId}
                targetThreadId={targetThreadId}
                onTargetMessageHandled={handleTargetMessageHandled}
                isDmChannelHint={isKnownDmChannel(stickyChannelId)}
                hidden={!isMultiplexActive}
              />
            </Suspense>
          )
        : mountedIds.map((id) => {
            const isActive = id === selectedChannel;
            // Resolve this instance's own workspace. Active uses the
            // user's current scope (matches pre-fix behavior exactly);
            // hidden instances use the channel's actual workspace so
            // cross-workspace kept-alive channels don't subscribe to
            // the active workspace's members. DMs fall back to
            // selectedWorkspace because they have no workspace_id.
            const instanceWorkspaceId = isActive
              ? selectedWorkspace
              : (channelWorkspaceMap.get(id) ?? selectedWorkspace);
            return (
              <div key={id} className={isActive ? "contents" : "hidden"}>
                <Suspense fallback={null}>
                  <MessageArea
                    channelId={id}
                    workspaceId={instanceWorkspaceId}
                    onSelectChannel={handleSelectChannelForMessageArea}
                    onToggleTerminal={handleToggleTerminal}
                    onOpenMobileSidebar={onOpenMobileSidebar}
                    targetMessageId={isActive ? targetMessageId : null}
                    targetThreadId={isActive ? targetThreadId : null}
                    onTargetMessageHandled={handleTargetMessageHandled}
                    isDmChannelHint={isKnownDmChannel(id)}
                    hidden={!isActive || !isMultiplexActive}
                  />
                </Suspense>
              </div>
            );
          })}
    </div>
  );
}
